export default class OrderCsvGenerator {
  constructor(config) {
    this.$container = config.$container;
    this.zipUrl = undefined;
    this.contactFileName = undefined;
    this.exportId = config.exportId;
    // Staleness tracking: if nothing about the response changes for STALE_MS
    // (no zip, no image/audit progress, no heartbeat), the job is likely stuck.
    this.stopped = false;
    this.lastSignature = undefined;
    this.lastActivityAt = null;
  }

  // How long with zero observable progress before we warn the user the export
  // may be stuck. Kept generous — a cold-cache audit legitimately scans for
  // several minutes, but it heartbeats while alive, so a frozen heartbeat trips
  // this only when the job has actually stopped.
  static get STALE_MS() { return 120000; }

  init() {
    $('#failed-retry, #stuck-retry').on('click', (e) => {
      e.preventDefault();
      // Return to the export form (selections typically preserved by the
      // browser) so the user can re-run without hunting for the page.
      window.history.back();
    });
    this._initListeners();
    return this;
  }

  _initListeners() {
    this.$container.hide();
    this.pollEndpoint();
  }

  pollEndpoint() {
    if (this.stopped) return;

    if (this.zipUrl) {
      if (this.$container.is(":visible")) return;

      $('#loading-message').hide();
      $('#audit-scan').hide();
      $('#stuck-warning').hide();
      this.$container.show();
      $('#contact-length').html(this.count);

      return;
    }

    $.get(`/admin/orders/waiting.json?export_id=${this.exportId}`, (data) => {
      if (this.handleFailure(data)) return; // terminal — stop polling
      this.renderAuditScan(data);
      this.renderStuckWarning(data);
      this.renderAuditResults(data);
      this.renderOrderProgress(data);
      if (data.chunked) {
        this.handleChunkedResponse(data);
      } else {
        this.handleSingleResponse(data);
      }
    });
  }

  // Terminal failure surfaced by the worker (stage === 'failed'). Stops polling
  // and shows the error + a retry affordance instead of spinning forever.
  handleFailure(data) {
    const stage = data.progress && data.progress.stage;
    if (stage !== 'failed') return false;

    this.stopped = true;
    $('#loading-message').hide();
    $('#audit-scan').hide();
    $('#stuck-warning').hide();
    $('#export-failed-msg').text(data.progress.error || 'The export job stopped unexpectedly.');
    $('#export-failed').show();
    return true;
  }

  // Live "scanning X of Y" bar for the audited export's S3 sweep. Hidden once
  // the audit yields real image/chunk progress or a downloadable zip.
  renderAuditScan(data) {
    const p = data.progress || {};
    const scanning = p.audit_prefixes_total != null && !data.chunked &&
      !p.total_images && !data.url;

    if (!scanning) {
      $('#audit-scan').hide();
      return;
    }

    $('#loading-message').hide();
    $('#audit-scan').show();
    const done = p.audit_prefixes_done || 0;
    const total = p.audit_prefixes_total;
    $('#audit-scan-done').text(done);
    $('#audit-scan-total').text(total);
    const pct = total ? Math.round((done / total) * 100) : 0;
    $('#audit-scan-bar').css('width', pct + '%');
  }

  // Warn when the export has shown no observable movement for STALE_MS. Any
  // change in the progress signature (stage, heartbeat, image/audit/chunk
  // counts) resets the timer, so a slow-but-alive export never trips it.
  renderStuckWarning(data) {
    const now = Date.now();
    const sig = this.progressSignature(data);

    if (this.lastSignature === undefined || sig !== this.lastSignature) {
      this.lastSignature = sig;
      this.lastActivityAt = now;
    }

    const stalled = !this.zipUrl && !data.url &&
      this.lastActivityAt != null &&
      (now - this.lastActivityAt) > OrderCsvGenerator.STALE_MS;

    $('#stuck-warning').toggle(!!stalled);
  }

  // A compact fingerprint of everything that changes while an export makes
  // progress. Unchanged across polls === no movement.
  progressSignature(data) {
    const p = data.progress || {};
    const parts = [
      p.stage, p.heartbeat_at, p.audit_prefixes_done, p.images_processed,
      p.orders_exported, p.orders_in_process, data.url
    ];
    if (data.chunks) parts.push(data.chunks.filter((c) => c.ready).length);
    return parts.join('|');
  }

  // Renders the per-order render progress panel (Total / Exported / In process
  // / Failed) for audited exports that request missing files from EC2. No-op
  // for exports without order-level render progress. Keeps polling while any
  // order is still rendering so the counts stay live.
  renderOrderProgress(data) {
    const p = data.order_progress;
    if (!p) return;

    $('#order-render-progress').show();
    $('#orders-total').text(p.total);
    $('#orders-exported').text(p.exported);
    $('#orders-in-process').text(p.in_process);
    $('#orders-failed').text(p.failed);
    $('#order-render-stage').text(p.in_process > 0 ? ' — building missing images, please wait…' : ' — complete');

    if (p.in_process > 0) {
      this.ordersRendering = true;
      setTimeout(this.pollEndpoint.bind(this), 5000);
    } else {
      this.ordersRendering = false;
    }
  }

  // Renders the pre-flight audit summary (ready/blocked counts) and the
  // missing-images CSV link when AuditedZipWorker has produced them. No-op
  // for non-audited exports.
  renderAuditResults(data) {
    const hasOrderProgress = !!data.order_progress;
    // For generate-missing exports the per-order panel below is the source of
    // truth. The audit's counts are order-PACKAGES (an order has several) and
    // they change as files render, so suppress them here and only surface the
    // missing-images CSV link. Non-generate-missing exports keep the old summary.
    if (!data.missing_csv_url && (hasOrderProgress || !data.audit_summary)) return;

    $('#audit-results').show();
    if (data.audit_summary && !hasOrderProgress) {
      $('#audit-ready').text(data.audit_summary.ready);
      $('#audit-blocked').text(data.audit_summary.blocked);
    } else {
      $('#audit-summary-counts').hide();
    }
    if (data.missing_csv_url) {
      $('#missing-csv-link').attr('href', data.missing_csv_url);
      $('#missing-csv-link-wrapper').show();
    }
  }

  handleSingleResponse(data) {
    this.zipUrl = data.url;
    this.fileName = data.file_name;
    this.count = data.count;

    // Update progress display if image export is in progress
    if (data.progress && data.progress.total_images) {
      const $progress = $('#progress-info');
      $progress.show();
      $('#total-orders').text(data.progress.total_orders);
      $('#total-images').text(data.progress.total_images);
      $('#images-processed').text(data.progress.images_processed || 0);

      const pct = Math.round(((data.progress.images_processed || 0) / data.progress.total_images) * 100);
      $('#progress-bar').css('width', pct + '%');
    }

    setTimeout(this.pollEndpoint.bind(this), 2500);
  }

  handleChunkedResponse(data) {
    $('#loading-message').text('Processing order chunks...');
    $('#chunks-container').show();
    $('#chunks-total').text(data.total_chunks);

    const readyCount = data.chunks.filter(c => c.ready).length;
    $('#chunks-ready').text(readyCount);

    const $list = $('#chunks-list');
    $list.empty();

    data.chunks.forEach((chunk) => {
      const num = chunk.chunk_index + 1;
      const label = `Chunk ${num} of ${data.total_chunks}`;

      if (chunk.ready) {
        $list.append(`<div style="margin: 8px 0; padding: 8px; background: #e8f5e9; border-radius: 4px;">
          <span style="color:green;">&#10003;</span> ${label} &mdash;
          <a href="/admin/orders/download_zip.csv?export_id=${chunk.export_id}"><strong>Download</strong></a>
        </div>`);
      } else {
        let progressText = 'Generating envelopes...';
        if (chunk.progress && chunk.progress.total_images) {
          const processed = chunk.progress.images_processed || 0;
          const pct = Math.round((processed / chunk.progress.total_images) * 100);
          progressText = `Images: ${processed} / ${chunk.progress.total_images} (${pct}%)`;
        } else if (chunk.progress && chunk.progress.stage === 'finalizing') {
          progressText = 'Finalizing ZIP...';
        }
        $list.append(`<div style="margin: 8px 0; padding: 8px; background: #fff3e0; border-radius: 4px;">
          <span style="color:orange;">&#9679;</span> ${label} &mdash; ${progressText}
        </div>`);
      }
    });

    // Never claim "ready/complete" while orders are still rendering — more
    // download chunks appear as those orders finish. Only the truly-done state
    // (no orders in process AND all current chunks zipped) says "all ready".
    const stillBuilding = data.order_progress && data.order_progress.in_process > 0;
    if (stillBuilding) {
      const n = data.order_progress.in_process;
      $('#loading-message').text(`Still building images for ${n} order(s) — more download chunks will appear as they finish. Do not refresh.`);
      setTimeout(this.pollEndpoint.bind(this), 2500);
    } else if (data.all_complete) {
      $('#loading-message').text('All orders ready — download the chunk(s) below.');
    } else {
      $('#loading-message').text('Processing order chunks…');
      setTimeout(this.pollEndpoint.bind(this), 2500);
    }
  }
}
